import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Hooks
import { useSetRecoilState } from 'recoil';
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
// Types
import { MonitorInfoStyles } from '../../containers/MonitorInfo';
// Types
import { outreachEventLocationsModalAtom } from '../../../../state';
import { GridItemType, OutreachEnum } from '../../types';
// Components
import { Grid, GridItem, PendingContent, Spinner } from 'plume-ui';
import StickyActionTable from './StickyActionTable/StickyActionTable';
import InfoCardBody from './renderers/InfoCardBody';
import InfoCardBodyGrid from './renderers/InfoCardBodyGrid';
// Helpers
import { DataRow } from 'plume-ui/dist/components/Table/Table';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { DependencyContainer } from '../../../../DependencyContainer';
import { Routes, trendRowDays } from '../../../../config';
import { useFeaturePermission } from '../../../../hooks/useFeaturePermission';
import { useFetchData } from '../../../../hooks/useFetchData';
import { MixPanelEvents } from '../../../../mixPanelEvents';
import { activePartnerAtom } from '../../../../state';
import { Permission } from '../../../../userPermissionConfig';
import { useRedirectToRoute } from '../../../../utils/hooks/useRedirectToRoute';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { parseSubscriberStatsData } from '../../helpers';
import {
  useGetOutreachStats,
  useGetTriggeredOutreachEvents,
} from '../../services/monitorServices';
import { outreachEventsTableHeaders } from './fixtures';
import { mapOutreachInfoCard, parseOutreachEventsTableData } from './helpers';
import SubscriberStatsRow from './renderers/SubscriberStatsRow';

type MonitorInfoWithOutreachProps = {
  monitorId: string;
  outreachConfig?: {
    subscriberPush?: {
      id?: string;
      status: keyof typeof OutreachEnum;
    };
    supportEmail?: {
      id?: string;
      status: keyof typeof OutreachEnum;
    };
    supportWebhook?: {
      id?: string;
      status: keyof typeof OutreachEnum;
    };
  };
};

type MonitorInfoWithOutreachStyles = Partial<MonitorInfoStyles> & {
  title: string;
};

const { outreachService } = new DependencyContainer();

const MonitorInfoWithOutreach: AdjustableComponent<
  MonitorInfoWithOutreachProps,
  MonitorInfoWithOutreachStyles
> = ({ outreachConfig }) => {
  const trackEvent = useTrackEvent();
  const redirectToRoute = useRedirectToRoute();
  const openOutreachEventLocationsModal = useSetRecoilState(
    outreachEventLocationsModalAtom,
  );
  const activePartner = useRecoilValue(activePartnerAtom);
  const { t } = useTranslation();
  const { hasPermission } = useFeaturePermission();
  const [supportOutreachInfo, setSupportOutreachInfo] = useState<
    GridItemType[]
  >([]);
  const [subscriberOutreachInfo, setSubscriberOutreachInfo] = useState<
    GridItemType[]
  >([]);
  const [supportOutreachWebhookInfo, setSupportOutreachWebhookInfo] = useState<
    GridItemType[]
  >([]);

  const { partnerId, monitorId, eventId } = useParams<{
    partnerId: string;
    monitorId: string;
    eventId: string;
  }>();
  const [triggeredOutreachTableData, setTriggeredOutreachTableData] = useState<
    DataRow[]
  >([]);

  const [rowReady, setRowReady] = useState(false);

  const openModalWithId = (id: string, locations: number) => {
    openOutreachEventLocationsModal({
      isOpen: true,
      id: id,
      locationsAmount: locations,
    });
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_EVENTS_MODAL_OPEN,
    });
    redirectToRoute(Routes.MonitorDetails, {
      partnerId,
      monitorId,
      eventId: id,
    });
  };

  useEffect(() => {
    if (eventId === '0' || !triggeredOutreachTableData.length) return;
    const event = triggeredOutreachTableData.find(
      (item) => item.outreachEvent === eventId,
    );
    if (!event) {
      redirectToRoute(Routes.MonitorDetails, {
        partnerId,
        monitorId,
        eventId: '0',
      });
    } else {
      openModalWithId(event.outreachEvent, +event.affectedLocations);
    }
  }, [triggeredOutreachTableData]);

  const [
    loadingSupportOutreachData,
    supportOutreachData,
    errorSupport,
  ] = useFetchData<any, any>(
    async () => {
      if (!activePartner?.id) return;
      if (!monitorId) return;
      return await outreachService.retrieveOutreachEmailConfig(
        activePartner.id,
        monitorId,
      );
    },
    undefined,
    [activePartner, monitorId],
    () => activePartner === undefined || monitorId === undefined,
  );
  const [
    loadingSubscriberOutreachData,
    subscriberOutreachData,
    errorSubscriber,
  ] = useFetchData<any, any>(
    async () => {
      if (!activePartner?.id) return;
      if (!monitorId) return;
      return await outreachService.retrieveOutreachPushConfig(
        activePartner.id,
        monitorId,
      );
    },
    undefined,
    [activePartner, monitorId],
    () => activePartner === undefined || monitorId === undefined,
  );
  const [
    loadingSupportOutreachWebhookData,
    supportOutreachWebhookData,
    errorWebhook,
  ] = useFetchData<any, any>(
    async () => {
      if (!activePartner?.id) return;
      if (!monitorId) return;
      return await outreachService.retrieveOutreachWebhookConfig(
        activePartner.id,
        monitorId,
      );
    },
    undefined,
    [activePartner, monitorId],
    () => activePartner === undefined || monitorId === undefined,
  );

  useEffect(() => {
    if (
      supportOutreachData === undefined ||
      loadingSupportOutreachData ||
      errorSupport
    )
      return;
    setSupportOutreachInfo(mapOutreachInfoCard(supportOutreachData[0]));
  }, [loadingSupportOutreachData]);

  useEffect(() => {
    if (
      subscriberOutreachData === undefined ||
      loadingSubscriberOutreachData ||
      errorSubscriber
    )
      return;
    setSubscriberOutreachInfo(mapOutreachInfoCard(subscriberOutreachData[0]));
  }, [loadingSubscriberOutreachData]);

  useEffect(() => {
    if (
      !supportOutreachWebhookData ||
      loadingSupportOutreachWebhookData ||
      errorWebhook
    )
      return;
    setSupportOutreachWebhookInfo(
      mapOutreachInfoCard(supportOutreachWebhookData[0]),
    );
  }, [loadingSupportOutreachWebhookData]);

  const {
    triggeredOutreachEventsDataLoading,
    triggeredOutreachEventsData,
  } = useGetTriggeredOutreachEvents(monitorId || '');

  useEffect(() => {
    if (!triggeredOutreachEventsData || triggeredOutreachEventsDataLoading)
      return;
    setTriggeredOutreachTableData(
      parseOutreachEventsTableData(triggeredOutreachEventsData),
    );
  }, [triggeredOutreachEventsData]);

  const [subscriberStatsData, setSubscriberStatsData] = useState<number[]>([]);

  const {
    outreachStatsDataLoading: outreachStatsLoading,
    outreachStatsData: outreachStats,
  } = useGetOutreachStats(monitorId, partnerId, trendRowDays);

  useEffect(() => {
    if (!outreachStatsLoading && outreachStats !== undefined) {
      setSubscriberStatsData(parseSubscriberStatsData(outreachStats));
      setRowReady(true);
    }
  }, [outreachStatsLoading, outreachStats]);

  return (
    <Grid>
      <GridItem colSpan="12" tabletColSpan="12">
        <div className="MonitorInfo__horizontalContainer">
          <InfoCardBodyGrid
            data={subscriberOutreachInfo}
            unconfig={subscriberOutreachInfo.length === 0}
            label={t('monitors.monitorInfo.subscriberOutreach')}
            loading={loadingSubscriberOutreachData}
            bodyProps={
              hasPermission(Permission.EditSubscriberPushOutreach)
                ? {
                    editRoute: {
                      route: Routes.OutreachEdit,
                      params: {
                        partnerId: partnerId,
                        monitorId: monitorId,
                        anchor:
                          outreachConfig?.subscriberPush?.status === 'unconfig'
                            ? 'outreach-enable'
                            : 'subscriber-outreach',
                      },
                    },
                  }
                : {}
            }
          />
          <InfoCardBody
            label={t('monitors.monitorInfo.subscriberStats.title')}
            optionalTitle={`(${t(
              'monitors.monitorInfo.subscriberStats.subtitle',
            )})`}
            labelTooltip={t('monitors.monitorInfo.subscriberStats.tooltip')}
            classes={(current) => {
              return {
                ...current,
                infoCard: `${current.infoCard} MonitorInfo__info-card__trend-row`,
              };
            }}
          >
            <SubscriberStatsRow values={subscriberStatsData} ready={rowReady} />
          </InfoCardBody>
        </div>
      </GridItem>
      {(outreachConfig?.subscriberPush?.status !== 'unconfig' ||
        outreachConfig?.supportEmail?.status !== 'unconfig' ||
        outreachConfig?.supportWebhook?.status !== 'unconfig') && (
        <>
          <GridItem colSpan="12" tabletColSpan="12">
            <div className="MonitorInfo__horizontalContainer">
              <InfoCardBodyGrid
                data={supportOutreachInfo}
                unconfig={supportOutreachInfo.length === 0}
                label={t('monitors.monitorInfo.supportOutreach')}
                loading={loadingSupportOutreachData}
                bodyProps={
                  hasPermission(Permission.EditSupportEmailOutreach)
                    ? {
                        editRoute: {
                          route: Routes.OutreachEdit,
                          params: {
                            partnerId: partnerId,
                            monitorId: monitorId,
                            anchor:
                              outreachConfig?.supportEmail?.status !==
                              'unconfig'
                                ? 'support-outreach'
                                : 'outreach-enable',
                          },
                        },
                      }
                    : {}
                }
              />
              <InfoCardBodyGrid
                data={supportOutreachWebhookInfo}
                unconfig={supportOutreachWebhookInfo.length === 0}
                label={t('monitors.monitorInfo.webhookOutreach')}
                loading={loadingSupportOutreachWebhookData}
                bodyProps={
                  hasPermission(Permission.EditSupportWebhookOutreach)
                    ? {
                        editRoute: {
                          route: Routes.OutreachEdit,
                          params: {
                            partnerId: partnerId,
                            monitorId: monitorId,
                            anchor:
                              outreachConfig?.supportWebhook?.status !==
                              'unconfig'
                                ? 'webhook-outreach'
                                : 'outreach-enable',
                          },
                        },
                      }
                    : {}
                }
              />
            </div>
          </GridItem>
          <GridItem colSpan="12" tabletColSpan="12">
            <InfoCardBody
              label={t('monitors.monitorInfo.triggeredOutreachEvents')}
              optionalTitle={`(${t('genericLabels.total')} ${
                triggeredOutreachTableData.length
              })`}
              classes={(current) => {
                return {
                  ...current,
                  infoCard: `${current.infoCard} MonitorInfo__info-card--squat`,
                };
              }}
            >
              <PendingContent
                loading={triggeredOutreachEventsDataLoading}
                loader={Spinner}
              >
                <StickyActionTable
                  truncateCellContent={false}
                  tableType="standard"
                  headerRow={outreachEventsTableHeaders(openModalWithId)}
                  dataRows={triggeredOutreachTableData}
                />
              </PendingContent>
            </InfoCardBody>
          </GridItem>
        </>
      )}
    </Grid>
  );
};

export default memo(MonitorInfoWithOutreach);
